import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

export type Theme = 'light' | 'dark' | 'system'
export type Currency = 'EUR' | 'USD' | 'CAD' | 'GBP' | 'JPY' | 'BRL' | 'AUD' | 'CHF'

interface AppState {
  theme: Theme
  currency: Currency
  timezone: string
  matchesDate: string // "yyyy-MM-dd"
  setTheme: (theme: Theme) => void
  setCurrency: (currency: Currency) => void
  setTimezone: (timezone: string) => void
  setMatchesDate: (date: string) => void
}

// SSR hazard fixed two ways vs. the original Vite version:
//   1. `timezone`/`matchesDate` used to be computed once at module-eval
//      time via Intl/Date — on the server that ran with the server's own
//      timezone/date, on the client with the browser's, guaranteeing a
//      hydration mismatch on first paint. They're now fixed, deterministic
//      placeholders ('UTC' / a fixed date) and get replaced with the real
//      values from a client-only useEffect (see AppStoreHydrator below),
//      i.e. after hydration, not during it.
//   2. `persist` reads localStorage to rehydrate — unavailable on the
//      server. `skipHydration: true` stops zustand from doing that
//      rehydration automatically during store creation/first render; we
//      call `.persist.rehydrate()` manually, again from a client-only
//      effect, so server and first client render both see the same
//      untouched defaults.
export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      theme: 'dark',
      currency: 'USD',
      timezone: 'UTC',
      matchesDate: '1970-01-01',
      setTheme: (theme) => set({ theme }),
      setCurrency: (currency) => set({ currency }),
      setTimezone: (timezone) => set({ timezone }),
      setMatchesDate: (date) => set({ matchesDate: date }),
    }),
    {
      name: 'ga-app-settings',
      storage: createJSONStorage(() => localStorage),
      skipHydration: true,
    }
  )
)
